/**
 * The town kit: scrap-and-salvage buildings, walls and props for the build mode (`?build`).
 * Each builder draws its piece centred on its footprint at facing 0, front towards +z, one unit
 * per tile (see PIECES in shared/content/town.ts for the footprints). Points that should glow
 * at night (windows, lamps) go in `g.userData.lights`; chimneys and stovepipes in
 * `g.userData.smoke`.
 */
import * as THREE from 'three';
import { BUILDERS, G, addMesh, mat } from './models.js';
import { crownGeo, trunkGeo } from './tree-models.js';
let APPLE_GEO: THREE.BufferGeometry[] | undefined;

const RUST = 0x8a4a2a, RUST2 = 0xa85a32, STEEL = 0x8a8e90, STEEL_D = 0x5e6266, TIMBER = 0x6b4a2b, PLANK = 0x8a6a42;
const CONCRETE = 0x9a968e, BRICK = 0x9a5a42, DARK = 0x262422, GLASS = 0xf3d483, TARP_B = 0x3a6a9a, TARP_G = 0x5a7a4a;
const WIN = { emissive: 0x6a4a10 };

type V3 = [number, number, number];
const light = (g: THREE.Group, p: V3) => { (g.userData.lights ??= [] as V3[]).push(p); };
const smoke = (g: THREE.Group, p: V3) => { (g.userData.smoke ??= [] as V3[]).push(p); };

/** A run of corrugation ribs over a face: along x (on a z-facing wall) or along z. */
function ribs(g: THREE.Object3D, cx: number, cy: number, cz: number, len: number, h: number, color: number, alongX = true, step = 0.22) {
  for (let t = -len / 2 + step / 2; t < len / 2; t += step) addMesh(g, G.sharp(alongX ? 0.05 : 0.04, h, alongX ? 0.04 : 0.05), color, alongX ? cx + t : cx, cy, alongX ? cz : cz + t);
}
/** A shipping container lying along x, centred at (x, y, z) with its base at y. */
function container(g: THREE.Object3D, x: number, y: number, z: number, len: number, depth: number, color: number, rib = 0x000000) {
  const h = 1.2, rc = rib || new THREE.Color(color).multiplyScalar(0.82).getHex();
  addMesh(g, G.box(len, h, depth), color, x, y + h / 2, z);
  ribs(g, x, y + h / 2, z + depth / 2 + 0.01, len - 0.2, h - 0.16, rc);
  ribs(g, x, y + h / 2, z - depth / 2 - 0.01, len - 0.2, h - 0.16, rc);
  // corner castings and a roof rim
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) addMesh(g, G.sharp(0.1, h + 0.02, 0.1), STEEL_D, x + sx * (len / 2 - 0.04), y + h / 2, z + sz * (depth / 2 - 0.04));
  addMesh(g, G.sharp(len + 0.04, 0.05, depth + 0.04), new THREE.Color(color).multiplyScalar(0.7).getHex(), x, y + h, z);
}
function door(g: THREE.Object3D, x: number, z: number, h = 0.95, color = PLANK) {
  addMesh(g, G.sharp(0.58, h + 0.06, 0.04), DARK, x, h / 2 + 0.12, z);
  addMesh(g, G.round(0.5, h, 0.05, 0.02), color, x, h / 2 + 0.12, z + 0.02);
  addMesh(g, G.sphere(0.03, 8), 0xd8b050, x + 0.17, 0.62, z + 0.06);
}
function windowAt(g: THREE.Group, x: number, y: number, z: number, w = 0.42, h = 0.34, boards = false) {
  addMesh(g, G.sharp(w + 0.08, h + 0.08, 0.04), DARK, x, y, z);
  addMesh(g, G.sharp(w, h, 0.05), GLASS, x, y, z + 0.01, WIN);
  if (boards) for (const t of [-0.1, 0.08]) { const b = addMesh(g, G.sharp(w + 0.14, 0.07, 0.04), PLANK, x, y + t, z + 0.04); b.rotation.z = t > 0 ? 0.25 : -0.2; }
  light(g, [x, y, z + 0.3]);
}
function stovepipe(g: THREE.Group, x: number, y: number, z: number, h = 0.8) {
  addMesh(g, G.cyl(0.07, 0.07, h, 8), STEEL_D, x, y + h / 2, z);
  addMesh(g, G.cyl(0.13, 0.13, 0.05, 8), STEEL_D, x, y + h + 0.06, z);
  smoke(g, [x, y + h + 0.1, z]);
}
/** A lean-to roof of corrugated sheet over (w × d), high at the back. */
function sheetRoof(g: THREE.Object3D, y: number, w: number, d: number, color = STEEL, tilt = 0.18) {
  const r = addMesh(g, G.sharp(w, 0.05, d), color, 0, y, 0); r.rotation.x = tilt;
  for (let t = -w / 2 + 0.11; t < w / 2; t += 0.22) { const k = addMesh(g, G.sharp(0.04, 0.05, d), new THREE.Color(color).multiplyScalar(0.85).getHex(), t, y + 0.03, 0); k.rotation.x = tilt; }
}
function tyre(g: THREE.Object3D, x: number, y: number, z: number, r = 0.26) {
  const m = new THREE.Mesh(new THREE.TorusGeometry(r, r * 0.42, 6, 12), mat(0x242424));
  m.rotation.x = Math.PI / 2; m.position.set(x, y, z); m.castShadow = true; m.receiveShadow = true; g.add(m); return m;
}
function ladder(g: THREE.Object3D, x: number, z: number, h: number, alongX = false) {
  for (const s of [-0.16, 0.16]) addMesh(g, G.sharp(0.04, h, 0.04), STEEL_D, alongX ? x + s : x, h / 2, alongX ? z : z + s);
  for (let y = 0.25; y < h; y += 0.3) addMesh(g, G.sharp(alongX ? 0.34 : 0.03, 0.03, alongX ? 0.03 : 0.34), STEEL_D, x, y, z);
}

Object.assign(BUILDERS, {
  container_home(g: THREE.Group) {
    addMesh(g, G.sharp(2.9, 0.12, 1.7), TIMBER, 0, 0.06, 0);
    container(g, 0, 0.12, 0, 2.8, 1.5, 0x3a5a7a);
    door(g, -0.6, 0.77);
    windowAt(g, 0.55, 0.85, 0.77);
    const aw = addMesh(g, G.sharp(1.0, 0.04, 0.3), RUST2, -0.6, 1.4, 0.86); aw.rotation.x = 0.3;
    stovepipe(g, 0.9, 1.33, -0.35);
    addMesh(g, G.box(0.36, 0.26, 0.22), PLANK, -0.6, 0.13, 0.87);
  },
  container_stack(g: THREE.Group) {
    container(g, 0, 0, 0, 2.8, 1.5, 0x4a6a4a);
    const top = new THREE.Group(); top.position.set(0.15, 1.22, -0.05); top.rotation.y = 0.04; g.add(top);
    container(top, 0, 0, 0, 2.6, 1.4, 0x8a3a2a);
    door(g, -0.7, 0.77); windowAt(g, 0.6, 0.8, 0.77);
    windowAt(g, 0.1, 0.62 + 1.22, 0.74, 0.5, 0.3);
    ladder(g, 1.48, 0.3, 2.4);
    for (const x of [-1.2, -0.4, 0.4, 1.2]) addMesh(g, G.sharp(0.04, 0.4, 0.04), STEEL_D, x, 2.62, 0.68);
    addMesh(g, G.sharp(2.5, 0.04, 0.04), STEEL_D, 0, 2.82, 0.68);
    stovepipe(g, -1.0, 2.44, -0.3);
  },
  bus_home(g: THREE.Group) {
    addMesh(g, G.round(3.8, 1.3, 1.5, 0.22), 0xc8a040, 0, 0.88, 0);
    addMesh(g, G.round(3.82, 0.18, 1.52, 0.05), 0x5a5048, 0, 0.3, 0);
    addMesh(g, G.round(3.7, 0.4, 1.52, 0.08), 0x2a3038, 0, 1.18, 0);
    for (let i = 0; i < 6; i++) { const x = -1.5 + i * 0.6; if (i % 2) windowAt(g, x, 1.18, 0.77, 0.4, 0.3, i === 3); else { const b = addMesh(g, G.sharp(0.46, 0.32, 0.04), PLANK, x, 1.18, 0.77); b.rotation.z = (i - 2.5) * 0.04; } }
    door(g, 1.5, 0.76, 0.95, 0x2a3038);
    for (const x of [-1.3, 1.1]) for (const z of [-0.62, 0.62]) tyre(g, x, 0.2, z, 0.2);
    addMesh(g, G.sharp(2.2, 0.05, 1.2), STEEL_D, -0.4, 1.58, 0);
    const tarp = addMesh(g, G.box(1.4, 0.25, 1.0), TARP_B, -0.6, 1.72, 0); tarp.rotation.z = 0.05;
    stovepipe(g, 0.9, 1.53, -0.3, 0.6);
  },
  brick_house(g: THREE.Group) {
    addMesh(g, G.box(2.7, 1.6, 2.7), BRICK, 0, 0.8, 0);
    // timber patches where the brick fell in, and courses picked out
    addMesh(g, G.sharp(0.9, 0.7, 0.06), PLANK, 0.7, 1.15, 1.36);
    addMesh(g, G.sharp(0.06, 0.8, 1.0), PLANK, -1.36, 1.0, -0.4);
    for (const y of [0.4, 0.8, 1.2]) addMesh(g, G.sharp(2.72, 0.03, 2.72), 0x8a4a36, 0, y, 0);
    door(g, -0.55, 1.36); windowAt(g, 0.7, 0.85, 1.38);
    windowAt(g, -1.38, 0.95, 0.5);
    for (const s of [-1, 1]) { const r = addMesh(g, G.sharp(3.0, 0.06, 1.6), STEEL, 0, 1.95, s * 0.7); r.rotation.x = s * 0.5; }
    addMesh(g, G.box(0.35, 0.9, 0.35), 0x8a5a42, 0.8, 2.2, -0.6);
    smoke(g, [0.8, 2.7, -0.6]);
  },
  scrap_hut(g: THREE.Group) {
    for (const [x, z] of [[-0.85, -0.85], [0.85, -0.85], [-0.85, 0.85], [0.85, 0.85]]) addMesh(g, G.sharp(0.08, 1.4, 0.08), TIMBER, x, 0.7, z);
    addMesh(g, G.sharp(1.75, 1.25, 0.05), STEEL, 0, 0.64, -0.86); ribs(g, 0, 0.64, -0.83, 1.6, 1.15, STEEL_D);
    for (const s of [-1, 1]) { addMesh(g, G.sharp(0.05, 1.25, 1.75), s < 0 ? RUST : STEEL, s * 0.86, 0.64, 0); }
    addMesh(g, G.sharp(1.75, 1.25, 0.05), RUST2, 0.45, 0.64, 0.86);
    addMesh(g, G.sharp(0.7, 1.05, 0.04), TARP_G, -0.45, 0.55, 0.87);
    windowAt(g, 0.45, 0.85, 0.9, 0.34, 0.26);
    const roof = new THREE.Group(); roof.position.y = 1.42; g.add(roof); sheetRoof(roof, 0, 1.94, 1.94, RUST2, -0.16);
    stovepipe(g, 0.55, 1.4, -0.55, 0.5);
  },
  tower_house(g: THREE.Group) {
    for (const [x, z] of [[-0.75, -0.75], [0.75, -0.75], [-0.75, 0.75], [0.75, 0.75]]) addMesh(g, G.sharp(0.08, 2.4, 0.08), STEEL_D, x, 1.2, z);
    for (const y of [0.8, 1.6]) for (const s of [-1, 1]) { const b = addMesh(g, G.sharp(1.6, 0.04, 0.04), STEEL_D, 0, y, s * 0.75); b.rotation.z = 0.5; const c = addMesh(g, G.sharp(0.04, 0.04, 1.6), STEEL_D, s * 0.75, y, 0); c.rotation.x = 0.5; }
    addMesh(g, G.sharp(1.8, 0.08, 1.8), PLANK, 0, 2.42, 0);
    addMesh(g, G.box(1.3, 0.9, 1.3), 0x7a6a52, 0, 2.9, 0);
    windowAt(g, 0, 2.95, 0.66, 0.5, 0.3);
    const roof = new THREE.Group(); roof.position.y = 3.42; g.add(roof); sheetRoof(roof, 0, 1.7, 1.7, RUST, 0.2);
    ladder(g, 0, 0.95, 2.4, true);
  },
  bank_hall(g: THREE.Group) {
    addMesh(g, G.box(2.8, 1.5, 1.7), CONCRETE, 0, 0.75, 0);
    addMesh(g, G.sharp(3.0, 0.14, 1.9), 0x8a867e, 0, 1.55, 0);
    // the vault door: a great round wheel of steel
    addMesh(g, G.cyl(0.55, 0.55, 0.1, 16).rotateX(Math.PI / 2), STEEL, 0, 0.72, 0.87);
    addMesh(g, G.cyl(0.42, 0.42, 0.06, 16).rotateX(Math.PI / 2), 0x9aa0a4, 0, 0.72, 0.93);
    for (let i = 0; i < 3; i++) { const s = addMesh(g, G.sharp(0.6, 0.05, 0.05), 0xc8b060, 0, 0.72, 0.98); s.rotation.z = (i * Math.PI) / 3; }
    for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; addMesh(g, G.sphere(0.035, 6), 0xc8b060, Math.cos(a) * 0.5, 0.72 + Math.sin(a) * 0.5, 0.94); }
    // a painted sign board over it, and lamps either side
    addMesh(g, G.sharp(1.3, 0.3, 0.05), 0xe8d8a0, 0, 1.3, 0.87);
    for (let i = 0; i < 4; i++) addMesh(g, G.sharp(0.16, 0.16, 0.02), 0x2a3a4a, -0.36 + i * 0.24, 1.3, 0.9);
    for (const s of [-1, 1]) { addMesh(g, G.sharp(0.06, 0.06, 0.25), STEEL_D, s * 1.0, 1.15, 0.95); addMesh(g, G.box(0.14, 0.18, 0.14), GLASS, s * 1.0, 1.08, 1.06, WIN); light(g, [s * 1.0, 1.08, 1.2]); }
    for (const s of [-1, 1]) addMesh(g, G.box(0.3, 0.6, 0.3), 0x7a766e, s * 1.25, 0.3, 0.95);
    // the roof: a low parapet, an old air unit, a tarp over a leak, an aerial; stains down the walls
    for (const s of [-1, 1]) { addMesh(g, G.sharp(3.0, 0.16, 0.1), 0x7e7a72, 0, 1.68, s * 0.9); addMesh(g, G.sharp(0.1, 0.16, 1.9), 0x7e7a72, s * 1.45, 1.68, 0); }
    addMesh(g, G.box(0.6, 0.35, 0.45), 0x9aa0a4, -0.8, 1.8, -0.3); ribs(g, -0.8, 1.8, -0.07, 0.5, 0.3, STEEL_D, true, 0.08);
    const tp = addMesh(g, G.sharp(0.9, 0.05, 0.7), TARP_B, 0.6, 1.65, 0.15); tp.rotation.z = 0.05;
    for (const [x, z] of [[0.2, -0.15], [1.0, 0.45]]) addMesh(g, G.box(0.14, 0.12, 0.14), 0x5a5a5a, x, 1.7, z);
    addMesh(g, G.sharp(0.03, 0.9, 0.03), STEEL_D, 1.1, 2.07, -0.6); addMesh(g, G.sharp(0.4, 0.02, 0.02), STEEL_D, 1.1, 2.4, -0.6);
    for (const x of [-1.1, -0.3, 0.9]) addMesh(g, G.sharp(0.1, 0.5 + Math.random() * 0.4, 0.02), 0x6e6a62, x, 1.2, 0.86);
  },
  smithy(g: THREE.Group) {
    addMesh(g, G.sharp(2.9, 0.08, 1.9), 0x5a5048, 0, 0.04, 0);
    addMesh(g, G.sharp(2.8, 1.6, 0.06), STEEL, 0, 0.8, -0.9); ribs(g, 0, 0.8, -0.86, 2.6, 1.5, STEEL_D);
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.06, 1.6, 1.8), s < 0 ? RUST : STEEL, s * 1.4, 0.8, 0);
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.1, 1.9, 0.1), TIMBER, s * 1.38, 0.95, 0.88);
    const roof = new THREE.Group(); roof.position.y = 1.85; g.add(roof); sheetRoof(roof, 0, 3.2, 2.2, RUST2, -0.22);
    // a tool rack and a quench barrel inside
    addMesh(g, G.sharp(1.2, 0.06, 0.1), TIMBER, -0.4, 1.2, -0.82);
    for (let i = 0; i < 4; i++) addMesh(g, G.sharp(0.05, 0.4, 0.03), STEEL_D, -0.85 + i * 0.3, 1.0, -0.78);
    addMesh(g, G.cyl(0.25, 0.25, 0.55, 10), RUST, 1.0, 0.3, -0.4);
    addMesh(g, G.box(0.4, 1.4, 0.4), 0x6a5a50, 0.9, 2.2, -0.6);
    smoke(g, [0.9, 2.95, -0.6]);
  },
  cook_house(g: THREE.Group) {
    container(g, 0, 0.1, -0.1, 2.8, 1.4, 0x5a7a5a);
    addMesh(g, G.sharp(2.9, 0.1, 1.6), TIMBER, 0, 0.05, -0.1);
    // the serving hatch, propped open, with a counter under it
    addMesh(g, G.sharp(1.2, 0.5, 0.05), DARK, -0.2, 0.95, 0.61);
    addMesh(g, G.sharp(1.2, 0.5, 0.05), GLASS, -0.2, 0.95, 0.6, WIN); light(g, [-0.2, 0.95, 0.9]);
    const flap = addMesh(g, G.sharp(1.3, 0.05, 0.36), 0x5a7a5a, -0.2, 1.3, 0.78); flap.rotation.x = 0.35;
    addMesh(g, G.sharp(1.4, 0.06, 0.26), PLANK, -0.2, 0.68, 0.74);
    door(g, 1.0, 0.62);
    stovepipe(g, -0.9, 1.3, -0.4, 0.9);
    stovepipe(g, 0.3, 1.3, -0.4, 0.6);
  },
  workshop(g: THREE.Group) {
    for (const [x, z] of [[-1.3, -0.8], [1.3, -0.8], [-1.3, 0.8], [1.3, 0.8]]) addMesh(g, G.sharp(0.1, 1.6, 0.1), TIMBER, x, 0.8, z);
    const tarp = addMesh(g, G.sharp(2.9, 0.04, 1.9), TARP_B, 0, 1.68, 0); tarp.rotation.x = -0.12;
    addMesh(g, G.sharp(2.6, 0.9, 0.05), PLANK, 0, 0.45, -0.82);
    for (let i = 0; i < 5; i++) addMesh(g, G.cyl(0.11, 0.11, 0.9, 8).rotateZ(Math.PI / 2), 0x8a5a32, -0.8, 0.12 + (i % 3) * 0.2, -0.5 + Math.floor(i / 3) * 0.22);
    addMesh(g, G.sharp(1.0, 0.06, 0.5), PLANK, 0.6, 0.75, -0.4);
    for (let i = 0; i < 3; i++) { const b = addMesh(g, G.sharp(0.04, 1.1, 0.04), 0xa87a48, 0.25 + i * 0.12, 0.9, -0.76); b.rotation.z = 0.15; }
  },
  water_tower(g: THREE.Group) {
    for (const [x, z] of [[-0.7, -0.7], [0.7, -0.7], [-0.7, 0.7], [0.7, 0.7]]) { const l = addMesh(g, G.sharp(0.1, 2.6, 0.1), STEEL_D, x * 0.9, 1.3, z * 0.9); l.rotation.set(z * 0.05, 0, -x * 0.05); }
    for (const s of [-1, 1]) { const b = addMesh(g, G.sharp(1.4, 0.04, 0.04), STEEL_D, 0, 1.2, s * 0.65); b.rotation.z = 0.6; }
    addMesh(g, G.cyl(0.85, 0.85, 1.2, 14), RUST, 0, 3.2, 0);
    ribs(g, 0, 3.2, 0.86, 1.0, 1.1, RUST2);
    addMesh(g, G.cone(0.95, 0.4, 14), STEEL_D, 0, 4.0, 0);
    ladder(g, 0, 0.8, 2.6, true);
  },
  // walls and fences on their own (a single tile, running along x); placed ones join up via wallPiece
  scrap_wall(g: THREE.Group) { wallPiece(g, 'scrap_wall', 0, 0, 3, 5); },
  palisade(g: THREE.Group) { wallPiece(g, 'palisade', 0, 0, 3, 5); },
  tire_wall(g: THREE.Group) { wallPiece(g, 'tire_wall', 0, 0, 3, 5); },
  chain_fence(g: THREE.Group) { wallPiece(g, 'chain_fence', 0, 0, 3, 5); },
  hedge(g: THREE.Group) { wallPiece(g, 'hedge', 0, 0, 3, 5); },
  stone_wall(g: THREE.Group) { wallPiece(g, 'stone_wall', 0, 0, 3, 5); },
  wood_fence(g: THREE.Group) { wallPiece(g, 'wood_fence', 0, 0, 3, 5); },
  field_gate(g: THREE.Group) { wallPiece(g, 'field_gate', 0, 0, 3, 5); },
  gate(g: THREE.Group) { wallPiece(g, 'gate', 0, 0, 3, 5); },
  wall_tower(g: THREE.Group) { wallPiece(g, 'wall_tower', 0, 0, 3, 5); },
  // the loose fence round a raiders' camp: three tiles of scrap wall, squeezed into its span
  scrap_fence(g: THREE.Group) {
    const k = Math.floor(Math.random() * 1000);
    for (const x of [-1, 0, 1]) { const t = new THREE.Group(); wallPiece(t, 'scrap_wall', 10, 0, k + x, k); t.position.x = x * 0.86; t.scale.x = 0.86; g.add(t); }
  },
  // props
  barrels(g: THREE.Group) {
    for (const [x, z, c] of [[-0.18, -0.12, 0x2a5a8a], [0.2, -0.05, RUST], [0, 0.22, 0x3a6a3a]] as const) { addMesh(g, G.cyl(0.19, 0.19, 0.6, 10), c, x, 0.3, z); for (const y of [0.15, 0.45]) addMesh(g, G.cyl(0.2, 0.2, 0.03, 10), STEEL_D, x, y, z); }
  },
  crate_stack(g: THREE.Group) {
    addMesh(g, G.box(0.5, 0.42, 0.5), PLANK, -0.15, 0.21, 0); addMesh(g, G.box(0.42, 0.38, 0.42), 0x9a7a4a, 0.25, 0.19, 0.12);
    const t = addMesh(g, G.box(0.44, 0.36, 0.44), 0x7a5a32, -0.1, 0.6, 0.02); t.rotation.y = 0.3;
    addMesh(g, G.sharp(0.3, 0.1, 0.01), 0x2a2a2a, -0.15, 0.25, 0.26);
  },
  scrap_pile(g: THREE.Group) {
    const r = (i: number) => ((i * 9301 + 49297) % 233280) / 233280;
    for (let i = 0; i < 9; i++) { const m = addMesh(g, i % 3 ? G.sharp(0.3 + r(i) * 0.4, 0.06 + r(i + 1) * 0.1, 0.2 + r(i + 2) * 0.3) : G.cyl(0.07, 0.07, 0.5, 6), [STEEL, RUST, RUST2, STEEL_D][i % 4], (r(i + 3) - 0.5) * 0.7, 0.08 + r(i + 4) * 0.35, (r(i + 5) - 0.5) * 0.7); m.rotation.set(r(i + 6) * 2, r(i + 7) * 3, r(i + 8) * 2); }
    tyre(g, 0.3, 0.1, 0.25, 0.18);
  },
  car_wreck(g: THREE.Group) {
    addMesh(g, G.round(1.8, 0.5, 0.9, 0.12), RUST, 0, 0.35, 0);
    addMesh(g, G.round(1.0, 0.4, 0.82, 0.12), RUST2, -0.1, 0.78, 0);
    addMesh(g, G.sharp(0.7, 0.3, 0.84), DARK, -0.1, 0.8, 0);
    // herbs growing out of it
    for (let i = 0; i < 4; i++) addMesh(g, G.ico(0.16), 0x5a8a3a, 0.45 + (i % 2) * 0.25 - 0.1, 0.65, (Math.floor(i / 2) - 0.5) * 0.35);
  },
  street_lamp(g: THREE.Group) {
    addMesh(g, G.cyl(0.05, 0.07, 2.4, 8), STEEL_D, 0, 1.2, 0);
    addMesh(g, G.sharp(0.5, 0.05, 0.05), STEEL_D, 0.22, 2.38, 0);
    addMesh(g, G.box(0.22, 0.25, 0.22), GLASS, 0.45, 2.22, 0, WIN);
    addMesh(g, G.cone(0.2, 0.14, 4), STEEL_D, 0.45, 2.4, 0).rotation.y = Math.PI / 4;
    light(g, [0.45, 2.2, 0]);
  },
  sign(g: THREE.Group) {
    addMesh(g, G.cyl(0.04, 0.04, 1.6, 8), STEEL, 0, 0.8, 0);
    addMesh(g, G.sharp(0.8, 0.5, 0.04), 0x2a6a4a, 0, 1.4, 0.03);
    addMesh(g, G.sharp(0.7, 0.4, 0.02), 0xe8e0c8, 0, 1.4, 0.06);
    for (let i = 0; i < 3; i++) addMesh(g, G.sharp(0.5 - i * 0.1, 0.05, 0.01), 0x3a3028, -0.05, 1.5 - i * 0.1, 0.075);
  },
  planter(g: THREE.Group) {
    addMesh(g, G.round(0.9, 0.4, 0.5, 0.15), 0xe8e4dc, 0, 0.3, 0);
    for (const x of [-0.35, 0.35]) for (const z of [-0.15, 0.15]) addMesh(g, G.sphere(0.05, 6), 0x9a9a9a, x, 0.06, z);
    addMesh(g, G.sharp(0.8, 0.06, 0.4), 0x4a3a2a, 0, 0.48, 0);
    for (let i = 0; i < 5; i++) addMesh(g, G.ico(0.12), [0x5a9a3a, 0x7aaa4a, 0xa83a2a][i % 3], -0.3 + i * 0.15, 0.58, (i % 2 - 0.5) * 0.15);
  },
  washing_line(g: THREE.Group) {
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.06, 1.7, 0.06), TIMBER, s * 0.9, 0.85, 0);
    addMesh(g, G.sharp(1.8, 0.015, 0.015), 0xd8d0c0, 0, 1.6, 0);
    for (let i = 0; i < 5; i++) addMesh(g, G.sharp(0.22, 0.3 + (i % 2) * 0.12, 0.02), [0xc84a3a, 0xe8e0c8, 0x3a6a9a, 0xd8b040, 0x5a7a4a][i], -0.6 + i * 0.3, 1.42 - (i % 2) * 0.06, 0);
  },
  awning(g: THREE.Group) {
    // a trader's canopy: four poles, a tarp that sags between them, a scalloped edge, goods beneath
    for (const [x, z] of [[-0.9, -0.9], [0.9, -0.9], [-0.9, 0.9], [0.9, 0.9]]) addMesh(g, G.sharp(0.07, z < 0 ? 1.62 : 1.4, 0.07), TIMBER, x, z < 0 ? 0.81 : 0.7, z);
    const col = [0xb85a3a, TARP_G, TARP_B, 0xc8a040][Math.floor(Math.random() * 4)], edge = dim(col, 0.8);
    for (const [z, y, t] of [[-0.45, 1.56, 0.2], [0.45, 1.4, 0.16]] as const) { const p = addMesh(g, G.sharp(1.9, 0.03, 0.95), col, 0, y, z); p.rotation.x = t; }
    for (let i = 0; i < 9; i++) { const f = addMesh(g, G.sharp(0.19, 0.12, 0.02), i % 2 ? col : edge, -0.85 + i * 0.21, 1.27, 0.92); f.rotation.z = 0; }
    for (const s2 of [-1, 1]) { const rope = addMesh(g, G.sharp(0.012, 0.9, 0.012), 0xd8c9a8, s2 * 1.05, 1.15, 1.15); rope.rotation.x = 0.7; }
    addMesh(g, G.sharp(1.3, 0.06, 0.5), PLANK, 0, 0.62, -0.35);
    for (const x of [-0.5, 0.5]) addMesh(g, G.sharp(0.06, 0.6, 0.45), TIMBER, x, 0.3, -0.35);
    for (let i = 0; i < 4; i++) addMesh(g, G.box(0.22, 0.18, 0.2), [PLANK, 0x9a7a4a, 0x7a8a5a, 0xa8987a][i], -0.42 + i * 0.28, 0.75, -0.35);
    addMesh(g, G.round(0.34, 0.4, 0.28, 0.1), 0xb8a070, 0.6, 0.2, 0.1); addMesh(g, G.round(0.3, 0.34, 0.26, 0.1), 0xa8905e, 0.75, 0.17, 0.4);
  },
  turbine(g: THREE.Group) {
    addMesh(g, G.cyl(0.05, 0.07, 2.6, 8), STEEL_D, 0, 1.3, 0);
    addMesh(g, G.box(0.18, 0.18, 0.3), RUST, 0, 2.62, 0);
    for (let i = 0; i < 4; i++) { const b = addMesh(g, G.sharp(0.08, 0.6, 0.02), 0xd8d0c0, 0, 2.62, 0.17); b.rotation.z = (i * Math.PI) / 2; b.geometry.translate(0, 0.3, 0); }
    addMesh(g, G.sharp(0.04, 0.3, 0.3), STEEL, 0, 2.62, -0.25);
  },
  bench(g: THREE.Group) {
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.06, 0.45, 0.4), STEEL_D, s * 0.45, 0.22, 0);
    addMesh(g, G.sharp(1.0, 0.06, 0.38), 0x3a6a8a, 0, 0.45, 0);
    addMesh(g, G.sharp(1.0, 0.35, 0.05), 0x3a6a8a, 0, 0.72, -0.18);
  },
});

/* ---------------- the country: farms and woods ---------------- */
const BOARD = 0x8a3a2a, BOARD_D = 0x6a2a1e, HAY = 0xd8b860, BARK = 0x5a4430, MOSS = 0x5a7a3a, STONE = 0x8a8578;
Object.assign(BUILDERS, {
  barn(g: THREE.Group) {
    // drawn at half size (the piece doubles it): a red timber barn, gable to the front, big doors
    const W = 3.3, D = 2.3, H = 1.45;
    addMesh(g, G.sharp(W + 0.1, 0.1, D + 0.1), 0x6e6a60, 0, 0.05, 0);
    addMesh(g, G.box(W, H, D), BOARD, 0, 0.1 + H / 2, 0);
    for (let x = -W / 2 + 0.12; x < W / 2; x += 0.24) for (const z of [D / 2 + 0.01, -D / 2 - 0.01]) addMesh(g, G.sharp(0.025, H, 0.02), BOARD_D, x, 0.1 + H / 2, z);
    // gable ends
    const gable = new THREE.Shape(); gable.moveTo(-W / 2, 0); gable.lineTo(W / 2, 0); gable.lineTo(0, 0.85); gable.closePath();
    for (const z of [D / 2, -D / 2]) { const m = new THREE.Mesh(new THREE.ShapeGeometry(gable), mat(BOARD, { side: THREE.DoubleSide })); m.position.set(0, 0.1 + H, z); g.add(m); }
    // the roof: two tin slopes, rusted in streaks
    for (const s of [-1, 1]) { const rf = addMesh(g, G.sharp(W / 2 + 0.25, 0.06, D + 0.3), s < 0 ? STEEL : RUST2, s * W / 4, 0.1 + H + 0.43, 0); rf.rotation.z = -s * 0.48; }
    addMesh(g, G.sharp(0.12, 0.08, D + 0.32), STEEL_D, 0, 0.1 + H + 0.86, 0);
    // big doors, one rolled open on its rail, hay inside
    addMesh(g, G.sharp(1.3, 0.06, 0.06), STEEL_D, 0, 0.1 + H - 0.05, D / 2 + 0.06);
    addMesh(g, G.sharp(1.1, H - 0.15, 0.04), DARK, 0, 0.1 + (H - 0.15) / 2, D / 2 + 0.02);
    addMesh(g, G.sharp(0.62, H - 0.18, 0.05), BOARD_D, -0.85, 0.1 + (H - 0.18) / 2, D / 2 + 0.07);
    for (const [x, y] of [[-0.25, 0.3], [0.2, 0.3], [0, 0.62]]) { const b = addMesh(g, G.cyl(0.24, 0.24, 0.4, 10), HAY, x, 0.1 + y, D / 2 - 0.3); b.rotation.z = Math.PI / 2; }
    // a hay loft door, and a weathervane
    addMesh(g, G.sharp(0.4, 0.38, 0.04), BOARD_D, 0, 0.1 + H + 0.32, D / 2 + 0.02);
    addMesh(g, G.sharp(0.02, 0.5, 0.02), STEEL_D, 0, 0.1 + H + 1.1, 0);
    addMesh(g, G.sharp(0.3, 0.08, 0.02), DARK, 0.05, 0.1 + H + 1.3, 0);
    light(g, [0, 0.1 + H + 0.32, D / 2 + 0.1]);
  },
  hay_bales(g: THREE.Group) {
    for (const [x, y, z, ry] of [[-0.45, 0.4, 0, 0.1], [0.45, 0.4, 0.05, -0.08], [0, 1.15, 0, 0.05]]) { const b = addMesh(g, G.cyl(0.4, 0.4, 0.7, 12), HAY, x, y, z); b.rotation.set(Math.PI / 2, ry, 0); }
    for (const x of [-0.45, 0.45]) { const e = addMesh(g, G.cyl(0.3, 0.3, 0.72, 12), 0xc8a850, x, 0.4, 0); e.rotation.x = Math.PI / 2; }
  },
  trough(g: THREE.Group) {
    addMesh(g, G.round(1.5, 0.45, 0.6, 0.12), 0xd8d4c8, 0, 0.3, 0);
    addMesh(g, G.sharp(1.36, 0.02, 0.46), 0x4a7a98, 0, 0.47, 0, { emissive: 0x0a1a24 });
    for (const x of [-0.6, 0.6]) for (const z of [-0.22, 0.22]) addMesh(g, G.sphere(0.06, 6), 0x8a8a80, x, 0.06, z);
    addMesh(g, G.cyl(0.03, 0.03, 0.9, 6), STEEL_D, 0.7, 0.75, -0.25);
  },
  sawpit(g: THREE.Group) {
    // two trestles over a shallow pit, a trunk on them and the long saw standing in the cut
    addMesh(g, G.sharp(2.4, 0.04, 1.2), 0x5a4a32, 0, 0.02, 0);
    addMesh(g, G.sharp(2.0, 0.03, 0.7), 0xc8a870, 0, 0.04, 0);
    for (const x of [-0.8, 0.8]) for (const s of [-1, 1]) { const leg = addMesh(g, G.sharp(0.08, 1.0, 0.08), TIMBER, x, 0.45, s * 0.28); leg.rotation.x = s * 0.35; }
    for (const x of [-0.8, 0.8]) addMesh(g, G.sharp(0.12, 0.1, 0.5), TIMBER, x, 0.9, 0);
    const log = addMesh(g, G.cyl(0.26, 0.28, 2.6, 10), BARK, 0, 1.18, 0); log.rotation.z = Math.PI / 2;
    for (const x of [-1.3, 1.3]) { const e = addMesh(g, G.cyl(0.24, 0.24, 0.02, 10), 0xd8b880, x, 1.18, 0); e.rotation.z = Math.PI / 2; }
    addMesh(g, G.sharp(0.05, 1.3, 0.02), 0xa3aab1, 0.3, 1.0, 0.0);
    for (const y of [0.4, 1.62]) addMesh(g, G.sharp(0.3, 0.05, 0.05), TIMBER, 0.3, y, 0);
  },
  log_stack(g: THREE.Group) {
    const rows: [number, number][] = [[0, 4], [1, 3], [2, 2]];
    for (const [row, n] of rows) for (let k = 0; k < n; k++) {
      const z = (k - (n - 1) / 2) * 0.36, y = 0.18 + row * 0.31, l = addMesh(g, G.cyl(0.17, 0.18, 2.6, 8), (row + k) % 2 ? BARK : 0x6a5038, 0, y, z); l.rotation.z = Math.PI / 2;
      for (const x of [-1.3, 1.3]) { const e = addMesh(g, G.cyl(0.15, 0.15, 0.02, 8), 0xd8b880, x, y, z); e.rotation.z = Math.PI / 2; }
    }
    for (const x of [-1.15, 1.15]) for (const s of [-1, 1]) addMesh(g, G.sharp(0.07, 0.7, 0.07), TIMBER, x, 0.35, s * 0.8);
  },
  rock_outcrop(g: THREE.Group) {
    const parts: [number, number, number, number][] = [[-0.35, -0.2, 0.62, 3], [0.4, 0.15, 0.5, 7], [0.05, 0.45, 0.36, 11], [-0.5, 0.5, 0.26, 5]];
    for (const [x, z, r, sd] of parts) { const c = addMesh(g, G.crag(r, sd), [STONE, 0x7a766c, 0x9a958a][sd % 3], x, r * 0.55, z); c.scale.set(1.1, 0.8, 1); c.rotation.y = sd; }
    addMesh(g, G.sphere(0.2, 6), 0x9aa86a, -0.3, 0.84, -0.25).scale.set(1.6, 0.25, 1.2);
    addMesh(g, G.sphere(0.12, 6), 0xc8b860, 0.45, 0.62, 0.1).scale.set(1.4, 0.25, 1);
  },
  boulder(g: THREE.Group) {
    const b = addMesh(g, G.crag(0.48, 3), STONE, 0, 0.32, 0); b.scale.set(1.1, 0.75, 0.95);
    addMesh(g, G.sphere(0.16, 6), 0x9aa86a, 0.05, 0.62, 0).scale.set(1.5, 0.3, 1.3);
  },
  fallen_log(g: THREE.Group) {
    const l = addMesh(g, G.cyl(0.3, 0.36, 2.8, 10), BARK, 0, 0.3, 0); l.rotation.z = Math.PI / 2 - 0.04;
    addMesh(g, G.sharp(2.5, 0.08, 0.36), MOSS, 0.05, 0.6, 0).rotation.z = -0.04;
    const e = addMesh(g, G.cyl(0.3, 0.3, 0.02, 10), 0xc8a878, -1.41, 0.32, 0); e.rotation.z = Math.PI / 2;
    const br = addMesh(g, G.cyl(0.06, 0.09, 0.9, 6), BARK, 0.6, 0.65, 0.15); br.rotation.set(0.5, 0, 0.6);
    for (const x of [-0.9, 0.2]) addMesh(g, G.sphere(0.07, 6), 0xd8c8a0, x, 0.32, 0.34).scale.set(1, 0.4, 1);
  },
  stump(g: THREE.Group) {
    addMesh(g, G.cyl(0.26, 0.32, 0.38, 10), BARK, 0, 0.19, 0);
    addMesh(g, G.cyl(0.25, 0.25, 0.02, 10), 0xd8b880, 0, 0.39, 0);
    addMesh(g, G.cyl(0.14, 0.14, 0.021, 10), 0xc8a070, 0, 0.395, 0);
  },
});

/* ---------------- the shore, the river, the orchard, the chapel, the old road ---------------- */
const SAND = 0xc8b48a, SEA_BLUE = 0x3a6a8a, LEAF = 0x4f8a3a, LEAF2 = 0x3f7a30, APPLE = 0xc8302a, LIMESTONE = 0xa8a294, WHITE = 0xe8e4d8;
Object.assign(BUILDERS, {
  spoil_heap(g: THREE.Group) {
    const h = addMesh(g, G.dod(0.9), 0x8a7a62, 0, 0.35, 0); h.scale.set(1.15, 0.55, 1.05);
    for (const [x, z, r] of [[0.6, 0.4, 0.25], [-0.5, 0.5, 0.2], [0.2, -0.6, 0.22]] as V3[]) addMesh(g, G.crag(r, Math.round(x * 10 + 20)), 0x7a766c, x, r * 0.6, z);
    const sh = addMesh(g, G.sharp(0.05, 0.9, 0.05), TIMBER, -0.3, 0.75, -0.2); sh.rotation.z = 0.6;
    addMesh(g, G.sharp(0.18, 0.04, 0.24), STEEL_D, -0.62, 0.38, -0.2).rotation.z = 0.6;
  },
  boat_shed(g: THREE.Group) {
    // half size: a long open-fronted shed, a boat on the stocks inside
    const W = 2.8, D = 1.7, H = 1.05;
    addMesh(g, G.sharp(W, 0.08, D), PLANK, 0, 0.04, 0);
    addMesh(g, G.sharp(W, H, 0.06), 0x6a5a48, 0, H / 2, -D / 2);
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.06, H, D), 0x6a5a48, s * W / 2, H / 2, 0);
    for (let x = -W / 2 + 0.1; x < W / 2; x += 0.18) addMesh(g, G.sharp(0.02, H, 0.02), 0x4a3a2a, x, H / 2, -D / 2 + 0.04);
    for (const s of [-1, 1]) { const r = addMesh(g, G.sharp(W + 0.25, 0.05, D / 2 + 0.25), s < 0 ? STEEL : RUST, 0, H + 0.22, s * D / 4); r.rotation.x = s * 0.42; }
    const boat = new THREE.Group(); boat.position.set(0, 0.38, 0); g.add(boat);
    addMesh(boat, G.round(1.6, 0.36, 0.6, 0.16), 0x3a6a8a, 0, 0, 0);
    addMesh(boat, G.sharp(1.5, 0.04, 0.5), 0x8a6a42, 0, 0.17, 0);
    for (const x of [-0.6, 0, 0.6]) addMesh(g, G.sharp(0.1, 0.22, 0.7), TIMBER, x, 0.12, 0);
    addMesh(g, G.sharp(0.5, 0.3, 0.3), PLANK, -1.1, 0.15, -0.5);
    light(g, [0, H - 0.1, 0]);
  },
  watermill(g: THREE.Group) {
    // half size: a stone ground floor, a timber upper storey, a tin roof, and the wheel on the east side
    const W = 2.4, D = 2.0;
    addMesh(g, G.box(W, 0.95, D), LIMESTONE, -0.15, 0.48, 0);
    addMesh(g, G.box(W - 0.1, 0.8, D - 0.1), 0x8a6a42, -0.15, 1.35, 0);
    for (let x = -W / 2; x < W / 2 - 0.2; x += 0.2) addMesh(g, G.sharp(0.02, 0.8, D - 0.05), 0x6a4a2b, x, 1.35, 0);
    for (const s of [-1, 1]) { const r = addMesh(g, G.sharp(W + 0.3, 0.06, D / 2 + 0.3), s < 0 ? RUST : STEEL, -0.15, 2.0, s * D / 4); r.rotation.x = s * 0.5; }
    door(g, -0.6, D / 2 + 0.01, 0.8);
    windowAt(g, 0.3, 1.4, D / 2 + 0.02, 0.36, 0.3);
    windowAt(g, -0.6, 1.4, D / 2 + 0.02, 0.36, 0.3);
    // the wheel: a rim, spokes and paddles, half in the race
    const wheel = new THREE.Group(); wheel.position.set(W / 2 + 0.05, 0.75, 0); wheel.rotation.y = Math.PI / 2; g.add(wheel);
    const rim = addMesh(wheel, G.cyl(0.85, 0.85, 0.08, 18), 0x5a3d24, 0, 0, -0.2); rim.rotation.x = Math.PI / 2;
    const rim2 = addMesh(wheel, G.cyl(0.85, 0.85, 0.08, 18), 0x5a3d24, 0, 0, 0.2); rim2.rotation.x = Math.PI / 2;
    for (let k = 0; k < 10; k++) { const a = (k / 10) * Math.PI * 2; const pd = addMesh(wheel, G.sharp(0.06, 0.32, 0.46), 0x7a5634, Math.cos(a) * 0.82, Math.sin(a) * 0.82, 0); pd.rotation.z = a; const sp = addMesh(wheel, G.sharp(0.04, 0.82, 0.04), 0x5a3d24, Math.cos(a) * 0.41, Math.sin(a) * 0.41, 0); sp.rotation.z = a + Math.PI / 2; }
    const axle = addMesh(wheel, G.cyl(0.08, 0.08, 0.6, 8), STEEL_D, 0, 0, 0); axle.rotation.x = Math.PI / 2;
    // the leat that feeds it
    addMesh(g, G.sharp(0.5, 0.08, 0.3), 0x6a4a2b, W / 2 + 0.05, 1.68, -0.3);
    smoke(g, [-0.9, 2.4, -0.4]);
    light(g, [0.3, 1.4, D / 2 + 0.1]);
  },
  fish_rack(g: THREE.Group) {
    for (const x of [-1.3, 0, 1.3]) addMesh(g, G.sharp(0.08, 1.2, 0.08), TIMBER, x, 0.6, 0);
    for (const y of [0.75, 1.1]) addMesh(g, G.sharp(2.7, 0.04, 0.04), TIMBER, 0, y, 0);
    for (let i = 0; i < 12; i++) { const x = -1.2 + (i % 6) * 0.48 + (i > 5 ? 0.24 : 0), y = i > 5 ? 0.94 : 0.59; const f = addMesh(g, G.sharp(0.1, 0.32, 0.03), i % 3 ? 0xb8a88a : 0x9a8a6a, x, y, 0.02); f.rotation.z = (i % 2 - 0.5) * 0.1; }
  },
  rowboat(g: THREE.Group) {
    const b = new THREE.Group(); b.rotation.z = 0.08; g.add(b);
    addMesh(b, G.round(2.4, 0.4, 0.9, 0.2), [0x3a6a8a, 0x8a3a2a, 0x5a7a4a][Math.floor(Math.random() * 3)], 0, 0.22, 0);
    addMesh(b, G.sharp(2.2, 0.05, 0.72), 0x8a6a42, 0, 0.4, 0);
    for (const x of [-0.4, 0.4]) addMesh(b, G.sharp(0.12, 0.05, 0.74), PLANK, x, 0.43, 0);
    for (const s of [-1, 1]) { const o = addMesh(g, G.sharp(1.6, 0.04, 0.06), PLANK, 0.1, 0.48, s * 0.2); o.rotation.y = s * 0.15; }
  },
  net_pile(g: THREE.Group) {
    const n = addMesh(g, G.dod(0.42), 0x6a7a6a, 0, 0.2, 0); n.scale.set(1.2, 0.5, 1);
    for (const [x, z] of [[0.3, 0.2], [-0.25, 0.3], [0.1, -0.35]]) addMesh(g, G.sphere(0.07, 6), 0xd8a030, x, 0.25, z);
    addMesh(g, G.round(0.3, 0.3, 0.3, 0.06), PLANK, 0.5, 0.15, -0.4);
  },
  driftwood(g: THREE.Group) {
    const a = addMesh(g, G.cyl(0.1, 0.14, 1.6, 6), 0xc8bea8, 0, 0.1, 0); a.rotation.set(0, 0.3, Math.PI / 2);
    const b = addMesh(g, G.cyl(0.05, 0.07, 0.7, 6), 0xbab09a, 0.5, 0.2, 0.2); b.rotation.set(0.5, 0, 1.0);
    addMesh(g, G.crag(0.12, 4), 0xd8d0c0, -0.7, 0.06, 0.3);
  },
  trawler(g: THREE.Group) {
    // a fishing boat on its side in the sand: the hull, a stub of mast, ribs where the planks have gone
    const b = new THREE.Group(); b.rotation.x = 0.55; b.position.y = 0.45; g.add(b);
    addMesh(b, G.round(3.6, 0.9, 1.5, 0.4), 0x2a4a5a, 0, 0, 0);
    addMesh(b, G.round(3.62, 0.2, 1.52, 0.1), 0xb8302a, 0, -0.35, 0);
    addMesh(b, G.sharp(3.2, 0.06, 1.3), 0x8a6a42, 0, 0.44, 0);
    addMesh(b, G.box(0.9, 0.6, 0.9), 0xd8d4c8, 0.6, 0.75, 0);
    for (const x of [-1.2, -0.9, -0.6]) addMesh(b, G.sharp(0.06, 0.95, 1.5), 0x3a2a1e, x, 0.02, 0.05);
    addMesh(b, G.sharp(0.5, 0.7, 0.04), DARK, -0.9, 0.05, 0.76);
    const mast = addMesh(b, G.cyl(0.06, 0.07, 1.4, 6), 0x6a5a48, -0.2, 1.1, 0); mast.rotation.z = 0.1;
    addMesh(g, G.sphere(0.9, 8), SAND, 0, -0.5, 0.6).scale.set(2.2, 0.6, 0.8);
    for (const [x, z] of [[1.6, -0.6], [-1.7, 0.7]]) addMesh(g, G.sphere(0.07, 6), 0xd8a030, x, 0.05, z);
  },
  apple_tree(g: THREE.Group) {
    // short and gnarled, wider than it is tall, apples in the leaves and a few in the grass
    const vc = (geo: THREE.BufferGeometry, c: number, x: number, y: number, z: number) => { const m = new THREE.Mesh(geo, mat(c, { vertexColors: true })); m.position.set(x, y, z); m.castShadow = m.receiveShadow = true; g.add(m); return m; };
    APPLE_GEO ??= [trunkGeo(0.08, 0.17, 1.0, { branches: 3, flare: 0.5, crook: 0.14, branchFrom: 0.5, branchLen: 0.5, seed: 80 }), crownGeo(0.78, { clumps: 11, squash: 0.72, spread: 0.62, seed: 81 }), crownGeo(0.42, { clumps: 5, squash: 0.8, seed: 82 })];
    vc(APPLE_GEO[0], 0x5a4030, 0, 0.5, 0);
    vc(APPLE_GEO[1], LEAF, 0, 1.25, 0);
    vc(APPLE_GEO[2], LEAF2, 0.05, 1.62, -0.15);
    for (let k = 0; k < 9; k++) { const a = k * 2.4; addMesh(g, G.sphere(0.065, 8), APPLE, Math.cos(a) * 0.74, 1.05 + (k % 3) * 0.18, Math.sin(a) * 0.74); }
    for (const [x, z] of [[0.5, 0.4], [-0.3, 0.55]]) addMesh(g, G.sphere(0.055, 6), APPLE, x, 0.05, z);
  },
  beehive(g: THREE.Group) {
    for (const [x, z] of [[-0.2, -0.2], [0.2, -0.2], [-0.2, 0.2], [0.2, 0.2]]) addMesh(g, G.sharp(0.05, 0.3, 0.05), TIMBER, x, 0.15, z);
    for (let k = 0; k < 3; k++) addMesh(g, G.box(0.55, 0.2, 0.5), k % 2 ? WHITE : 0xd8d0b8, 0, 0.42 + k * 0.21, 0);
    const roof = addMesh(g, G.sharp(0.7, 0.06, 0.62), STEEL, 0, 0.98, 0); roof.rotation.z = 0.08;
    addMesh(g, G.sharp(0.2, 0.03, 0.03), DARK, 0, 0.36, 0.26);
  },
  cider_press(g: THREE.Group) {
    addMesh(g, G.cyl(0.42, 0.45, 0.4, 12), 0x7a5634, -0.4, 0.2, 0);
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.1, 1.3, 0.1), TIMBER, -0.4 + s * 0.45, 0.65, 0);
    addMesh(g, G.sharp(1.05, 0.14, 0.16), TIMBER, -0.4, 1.3, 0);
    addMesh(g, G.cyl(0.05, 0.05, 0.6, 8), STEEL_D, -0.4, 1.0, 0);
    addMesh(g, G.sharp(0.7, 0.06, 0.06), STEEL_D, -0.4, 0.75, 0);
    addMesh(g, G.cyl(0.25, 0.28, 0.45, 10), 0x8a6a42, 0.55, 0.23, 0.1);
    for (const [x, z] of [[0.5, 0.0], [0.62, 0.2]]) addMesh(g, G.sphere(0.07, 6), APPLE, x, 0.5, z);
  },
  chapel_ruin(g: THREE.Group) {
    // half size: grey stone walls standing to different heights, a gable at the far end, the bell tower over the door
    const W = 2.0, D = 3.4, walls: [number, number, number, number, number][] = [
      [-W / 2, 0, 0.12, D, 1.1], [W / 2, -0.4, 0.12, D - 0.8, 0.8], [0, -D / 2, W, 0.12, 1.6],
    ];
    addMesh(g, G.sharp(W + 0.3, 0.08, D + 0.3), 0x8a8578, 0, 0.04, 0);
    for (const [x, z, w, d, h] of walls) addMesh(g, G.box(w, h, d), LIMESTONE, x, h / 2, z);
    // the broken tops
    for (let k = 0; k < 6; k++) addMesh(g, G.crag(0.12, k + 3), LIMESTONE, -W / 2, 1.1 + (k % 2) * 0.08, -D / 2 + 0.3 + k * 0.55);
    // the far gable
    const gab = new THREE.Shape(); gab.moveTo(-W / 2, 0); gab.lineTo(W / 2, 0); gab.lineTo(0, 0.7); gab.closePath();
    const gm = new THREE.Mesh(new THREE.ExtrudeGeometry(gab, { depth: 0.12, bevelEnabled: false }), mat(LIMESTONE)); gm.position.set(0, 1.6, -D / 2 - 0.06); g.add(gm);
    // a lancet window, dark
    addMesh(g, G.sharp(0.25, 0.6, 0.14), DARK, 0, 1.25, -D / 2);
    // the tower over the door: square, taller, a bell in the open top, the cap half gone
    const T = 0.8, TH = 2.6;
    addMesh(g, G.box(T, TH, T), 0x9a958a, 0, TH / 2, D / 2 - T / 2);
    door(g, 0, D / 2 + 0.01, 0.75, 0x4a3a2a);
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) addMesh(g, G.sharp(0.12, 0.6, 0.12), 0x9a958a, x * (T / 2 - 0.06), TH + 0.3, D / 2 - T / 2 + z * (T / 2 - 0.06));
    addMesh(g, G.cone(0.16, 0.22, 10), 0xa8783a, 0, TH + 0.35, D / 2 - T / 2);
    const cap = addMesh(g, G.cone(0.62, 0.75, 4), 0x5a5e62, 0, TH + 0.95, D / 2 - T / 2); cap.rotation.set(0.12, Math.PI / 4, 0.1);
    // ivy on the walls, and rubble
    for (const [x, y, z] of [[-W / 2 - 0.05, 0.6, 0.6], [W / 2 + 0.05, 0.4, -1.0], [0.4, 1.0, -D / 2 - 0.05]] as V3[]) addMesh(g, G.dod(0.28), 0x3f6a2e, x, y, z).scale.set(0.5, 1.2, 1.1);
    for (let k = 0; k < 5; k++) addMesh(g, G.crag(0.1, k), LIMESTONE, W / 2 + 0.2 + (k % 2) * 0.15, 0.08, 0.4 + k * 0.25);
  },
  gravestone(g: THREE.Group) {
    const s = addMesh(g, G.round(0.42, 0.62, 0.1, 0.08), 0x9a958a, 0, 0.3, 0); s.rotation.z = (Math.random() - 0.5) * 0.15;
    addMesh(g, G.sharp(0.5, 0.06, 0.6), 0x5a7a3a, 0, 0.02, 0.32);
    if (Math.random() < 0.4) addMesh(g, G.sphere(0.05, 6), [0xf0e050, 0xe8e8f0, 0xd06080][Math.floor(Math.random() * 3)], 0.1, 0.07, 0.25);
  },
  petrol_canopy(g: THREE.Group) {
    // drawn at half size: a flat tin canopy on four legs, the ROADHOUSE board along its front
    const W = 2.8, D = 1.8, H = 1.55;
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) addMesh(g, G.sharp(0.1, H, 0.1), 0xd8d4c8, x * (W / 2 - 0.15), H / 2, z * (D / 2 - 0.15));
    addMesh(g, G.box(W, 0.2, D), 0xd8d4c8, 0, H + 0.1, 0);
    addMesh(g, G.sharp(W + 0.02, 0.1, 0.03), 0xb8302a, 0, H + 0.1, D / 2 + 0.01);
    const board = addMesh(g, G.sharp(1.3, 0.18, 0.04), 0x2a2a2a, 0, H + 0.36, D / 2 - 0.05);
    void board;
    for (let k = 0; k < 9; k++) addMesh(g, G.sharp(0.08, 0.09, 0.02), 0xf1bf55, -0.5 + k * 0.125, H + 0.36, D / 2 - 0.02, WIN);
    light(g, [0, H - 0.05, 0]);
    // weeds through the cracks of the forecourt
    for (const [x, z] of [[-0.8, 0.4], [0.9, -0.5], [0.2, 0.7]]) addMesh(g, G.cone(0.08, 0.2, 5), 0x6a8a3a, x, 0.1, z);
  },
  fuel_pump(g: THREE.Group) {
    addMesh(g, G.sharp(0.5, 0.12, 0.4), 0x8a8a84, 0, 0.06, 0);
    addMesh(g, G.round(0.38, 1.0, 0.3, 0.05), [0xb8302a, 0x2a5a8a][Math.floor(Math.random() * 2)], 0, 0.62, 0);
    addMesh(g, G.sharp(0.26, 0.2, 0.02), 0xe8e4d8, 0, 0.85, 0.16);
    addMesh(g, G.sphere(0.12, 8), WHITE, 0, 1.24, 0);
    const hose = addMesh(g, G.cyl(0.02, 0.02, 0.6, 6), DARK, 0.22, 0.6, 0.05); hose.rotation.z = 0.3;
  },
});

/* ---------------- walls that join up ---------------- */
const dim = (c: number, k: number) => new THREE.Color(c).multiplyScalar(k).getHex();
const SHEETS = [STEEL, RUST2, RUST, 0x6a8494, 0x7a8a5a, 0xa8987a];
/** Run directions: bit, and the turn that points a run drawn along +x that way. */
const RUNS: [number, number][] = [[1, Math.PI / 2], [2, 0], [4, -Math.PI / 2], [8, Math.PI]];

/**
 * A wall or gate tile, joined to its neighbours. `join` is from wallJoins (N 1, E 2, S 4, W 8;
 * the same shifted up 4 for gates); with no neighbours it runs along its facing. Drawn in world
 * directions (don't turn the group by facing), centred on the tile, `seed` from the tile so each
 * looks a little different but always the same.
 */
export function wallPiece(g: THREE.Group, kind: string, join: number, facing: number, tx: number, tz: number) {
  let s = ((tx * 7919 + tz * 104729) * 2654435761) >>> 0 || 1;
  const rnd = () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
  const along = facing % 2 ? 5 : 10;
  if (kind === 'gate') return gatePiece(g, join, along, rnd);
  if (kind === 'field_gate') return fieldGatePiece(g, join, along, rnd);
  let m = join & 15;
  if (!m) m = along;
  if (m === 1 || m === 4) m |= 5;
  if (m === 2 || m === 8) m |= 10;
  const straight = m === 5 || m === 10;
  // runs: one per side it joins, drawn along +x from the middle to the edge
  const runs: THREE.Group[] = [];
  for (const [bit, turn] of RUNS) if (m & bit) { const r = new THREE.Group(); r.rotation.y = turn; g.add(r); runs.push(r); }
  if (kind === 'scrap_wall' || kind === 'wall_tower') {
    // the same sheets for a few tiles at a time, then a different batch (with the odd stray)
    const cell = Math.floor(tx / 4) * 31 + Math.floor(tz / 4) * 17, stray = rnd() < 0.18;
    const c = SHEETS[(stray ? Math.floor(rnd() * SHEETS.length) : ((cell % SHEETS.length) + SHEETS.length) % SHEETS.length)], rib = dim(c, 0.78);
    runs.forEach((r, i) => {
      const h = 0.98 + rnd() * 0.3, zo = (i % 2 ? 0.018 : -0.018), y0 = 0.1, pick = rnd();
      if (pick < 0.16) {
        // a stretch of salvaged planks instead of sheet
        for (let k = 0; k < 4; k++) { const ph = h - 0.1 + rnd() * 0.18, pl = addMesh(r, G.sharp(0.13, ph, 0.045), [PLANK, 0x7a5a38, 0x9a7a50][k % 3], 0.07 + k * 0.135, y0 + ph / 2, zo); pl.rotation.z = (rnd() - 0.5) * 0.06; }
      } else if (pick < 0.22) {
        // an old door, nailed in sideways
        addMesh(r, G.sharp(0.56, h - 0.1, 0.05), [0x3a5a7a, 0x8a3a2a, 0x5a6a3a][Math.floor(rnd() * 3)], 0.27, y0 + (h - 0.1) / 2, zo);
        addMesh(r, G.sphere(0.03, 6), 0xd8b050, 0.46, y0 + h * 0.45, zo + 0.035);
        addMesh(r, G.sharp(0.4, 0.3, 0.06), DARK, 0.27, y0 + h * 0.72, zo);
      } else {
        // a corrugated sheet per half, overlapping the post
        const sh = addMesh(r, G.sharp(0.6, h, 0.035), c, 0.27, y0 + h / 2, zo); sh.rotation.z = (rnd() - 0.5) * 0.05;
        for (const x of [0.08, 0.2, 0.32, 0.44]) for (const f of [-1, 1]) addMesh(r, G.sharp(0.035, h - 0.06, 0.02), rib, x, y0 + h / 2, zo + f * 0.025);
        if (rnd() < 0.5) addMesh(r, G.sharp(0.05, h * (0.3 + rnd() * 0.3), 0.045), RUST, 0.1 + rnd() * 0.3, y0 + h * 0.8, zo);
        if (rnd() < 0.3) { const px = 0.12 + rnd() * 0.24, py = 0.3 + rnd() * 0.5, pz = zo + (rnd() < 0.5 ? 0.03 : -0.03); addMesh(r, G.sharp(0.22, 0.22, 0.03), STEEL_D, px, py, pz); for (const [dx, dy] of [[-0.08, -0.08], [0.08, -0.08], [-0.08, 0.08], [0.08, 0.08]]) addMesh(r, G.sphere(0.016, 4), STEEL, px + dx, py + dy, pz + Math.sign(pz - zo) * 0.02); }
      }
      // a footing of stones and sandbags, timber rails behind, wire along the top
      addMesh(r, G.sharp(0.52, 0.12, 0.16), 0x6e6a60, 0.26, 0.06, 0);
      for (const y of [0.34, 0.98]) addMesh(r, G.sharp(0.5, 0.07, 0.06), TIMBER, 0.25, y, -0.07);
      addMesh(r, G.sharp(0.5, 0.015, 0.015), STEEL_D, 0.25, 1.32, 0);
      for (const x of [0.12, 0.36]) addMesh(r, G.sharp(0.06, 0.06, 0.012), STEEL_D, x, 1.32, 0).rotation.z = 0.8;
      if (rnd() < 0.12) for (let k = 0; k < 3; k++) { const sb = addMesh(r, G.round(0.2, 0.12, 0.16, 0.05), 0x9a8a62, 0.12 + k * 0.15, 0.06 + (k === 1 ? 0.11 : 0), 0.14); sb.rotation.y = (rnd() - 0.5) * 0.4; }
    });
    // the post: slim on a straight run, a stout capped one at a corner, an end or a join
    const w = straight ? 0.1 : 0.16;
    addMesh(g, G.sharp(w, 1.42, w), TIMBER, 0, 0.71, 0);
    if (!straight) addMesh(g, G.sharp(w + 0.06, 0.05, w + 0.06), STEEL_D, 0, 1.44, 0);
    // now and then a raking prop holds a straight run up
    if (straight && kind === 'scrap_wall' && rnd() < 0.18) {
      const side = rnd() < 0.5 ? 1 : -1, br = new THREE.Group(); br.rotation.y = m === 5 ? Math.PI / 2 : 0; g.add(br);
      const b2 = addMesh(br, G.sharp(0.07, 1.2, 0.07), TIMBER, 0, 0.55, side * 0.3); b2.rotation.x = side * 0.55;
    }
    if (kind === 'wall_tower') towerOn(g, rnd);
  } else if (kind === 'palisade') {  } else if (kind === 'palisade') {
    const wood = 0x7a5634, tip = 0x9a7a54, lash = 0x5a4024;
    const stake = (r: THREE.Object3D, x: number) => { const h = 1.3 + rnd() * 0.28; const k = addMesh(r, G.cyl(0.07, 0.085, h, 6), wood, x, h / 2, (rnd() - 0.5) * 0.03); k.rotation.z = (rnd() - 0.5) * 0.06; addMesh(r, G.cone(0.075, 0.22, 6), tip, x, h + 0.1, k.position.z); };
    stake(g, 0);
    for (const r of runs) { for (const x of [0.16, 0.32, 0.47]) stake(r, x); for (const y of [0.4, 0.95]) addMesh(r, G.sharp(0.5, 0.06, 0.05), lash, 0.25, y, 0.09); }
  } else if (kind === 'chain_fence') {
    addMesh(g, G.cyl(0.04, 0.04, 1.45, 8), STEEL, 0, 0.72, 0);
    addMesh(g, G.sphere(0.055, 8), STEEL, 0, 1.46, 0);
    for (const r of runs) {
      const rail = addMesh(r, G.cyl(0.022, 0.022, 0.5, 6), STEEL, 0.25, 1.36, 0); rail.rotation.z = Math.PI / 2;
      const net = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 1.18), mat(0x9aa0a4, { transparent: true, opacity: 0.32, side: THREE.DoubleSide, depthWrite: false }));
      net.position.set(0.25, 0.71 - rnd() * 0.04, 0); r.add(net);
      for (const x of [0.08, 0.25, 0.42]) for (const t of [0.72, -0.72]) { const w = addMesh(r, G.sharp(0.012, 0.86, 0.012), 0xb0b4b8, x, 0.7, 0); w.rotation.z = t; }
      addMesh(r, G.sharp(0.5, 0.012, 0.012), 0xb0b4b8, 0.25, 0.12, 0);
    }
  } else if (kind === 'hedge') {
    // laid thorn and hazel: a lumpy green mass, darker underneath, the odd flower or berry
    const greens = [0x3f6a2e, 0x46743a, 0x365e28, 0x4f7a36];
    const lump = (r: THREE.Object3D, x: number, k: number) => {
      const h = 0.95 + rnd() * 0.3, c = greens[Math.floor(rnd() * greens.length)];
      const b = addMesh(r, G.dod(0.34), c, x, h * 0.55, (rnd() - 0.5) * 0.08); b.scale.set(0.95 + rnd() * 0.3, h * 1.25, 1.05 + rnd() * 0.2);
      addMesh(r, G.dod(0.26), dim(c, 0.7), x + 0.05, 0.22, 0).scale.set(1.2, 0.9, 1.25);
      if (rnd() < 0.35 * k) addMesh(r, G.sphere(0.035, 5), rnd() < 0.5 ? 0xf0e8f0 : 0xb03030, x + (rnd() - 0.5) * 0.3, h * 0.6 + rnd() * 0.3, 0.3 * (rnd() < 0.5 ? 1 : -1));
    };
    lump(g, 0, 1);
    for (const r of runs) lump(r, 0.32, 1);
  } else if (kind === 'stone_wall') {
    // field stones stacked dry: a battered base, rounder stones above, a row of cope stones on top
    const greys = [0x8a8578, 0x7a766c, 0x9a958a, 0x6e6a62];
    const course = (r: THREE.Object3D, x0: number, len: number) => {
      addMesh(r, G.sharp(len, 0.24, 0.5), greys[3], x0 + len / 2, 0.12, 0);
      for (let k = 0; k < Math.max(1, Math.round(len / 0.17)); k++) { const st = addMesh(r, G.crag(0.13 + rnd() * 0.04, Math.floor(rnd() * 99)), greys[Math.floor(rnd() * 3)], x0 + 0.08 + k * 0.17, 0.34 + rnd() * 0.04, (rnd() - 0.5) * 0.08); st.scale.set(1.1, 0.75, 1.6); }
      for (let k = 0; k < Math.max(1, Math.round(len / 0.13)); k++) { const cp = addMesh(r, G.sharp(0.1, 0.2, 0.4), greys[Math.floor(rnd() * 3)], x0 + 0.06 + k * 0.13, 0.58, 0); cp.rotation.z = (rnd() - 0.5) * 0.3; }
      if (rnd() < 0.4) addMesh(r, G.sphere(0.07, 5), 0x8aa05a, x0 + rnd() * len, 0.66, (rnd() - 0.5) * 0.2).scale.set(1.4, 0.4, 1);
    };
    course(g, -0.15, 0.3);
    for (const r of runs) course(r, 0.1, 0.42);
  } else if (kind === 'wood_fence') {
    // split rails between posts, silvered with weather
    const wood = [0x8a7a5a, 0x7a6a4a, 0x968664][Math.floor(rnd() * 3)];
    const post = addMesh(g, G.sharp(0.11, 1.0, 0.11), dim(wood, 0.85), 0, 0.5, 0); post.rotation.z = (rnd() - 0.5) * 0.05;
    for (const r of runs) for (const y of [0.38, 0.78]) { const rail = addMesh(r, G.sharp(0.52, 0.07, 0.05), wood, 0.25, y + (rnd() - 0.5) * 0.03, 0.03); rail.rotation.z = (rnd() - 0.5) * 0.06; }
  } else if (kind === 'tire_wall') {
    const col = (r: THREE.Object3D, x: number) => { for (let y = 0; y < 3; y++) tyre(r, x + (rnd() - 0.5) * 0.04, 0.12 + y * 0.22, (rnd() - 0.5) * 0.04, 0.22); };
    col(g, 0);
    for (const r of runs) col(r, 0.36);
    if (rnd() < 0.5) addMesh(g, G.cyl(0.16, 0.18, 0.08, 8), 0x5a6a3a, 0, 0.72, 0);
  }
}

/** A lookout on the wall: four legs, a railed platform over the wall with a lantern, a ladder, a tin roof. */
function towerOn(g: THREE.Group, rnd: () => number) {
  const H = 2.15, P = 0.78;
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) { const l = addMesh(g, G.sharp(0.1, H + 0.9, 0.1), TIMBER, x * P * 0.82, (H + 0.9) / 2, z * P * 0.82); l.rotation.set(z * 0.03, 0, -x * 0.03); }
  for (const y of [0.7, 1.45]) for (const s of [-1, 1]) { const a = addMesh(g, G.sharp(P * 1.7, 0.05, 0.05), TIMBER, 0, y, s * P * 0.82); a.rotation.z = s * 0.55; const b = addMesh(g, G.sharp(0.05, 0.05, P * 1.7), TIMBER, s * P * 0.82, y, 0); b.rotation.x = s * 0.55; }
  addMesh(g, G.sharp(P * 2.1, 0.08, P * 2.1), PLANK, 0, H, 0);
  for (const s of [-1, 1]) { addMesh(g, G.sharp(P * 2.1, 0.32, 0.04), s < 0 ? RUST : STEEL, 0, H + 0.2, s * P * 1.05); addMesh(g, G.sharp(0.04, 0.32, P * 2.1), s < 0 ? STEEL : RUST2, s * P * 1.05, H + 0.2, 0); }
  const roof = new THREE.Group(); roof.position.y = H + 0.92; g.add(roof); sheetRoof(roof, 0, P * 2.3, P * 2.3, RUST2, 0.14 + rnd() * 0.1);
  // the lantern that burns all night, and a ladder up the inside
  addMesh(g, G.sharp(0.05, 0.3, 0.05), STEEL_D, P * 0.7, H + 0.5, P * 0.7);
  addMesh(g, G.box(0.13, 0.17, 0.13), GLASS, P * 0.7, H + 0.32, P * 0.7, WIN); light(g, [P * 0.7, H + 0.32, P * 0.7]);
  ladder(g, 0, -P * 0.95, H, true);
}

/** A five-bar field gate: a stout post where it meets the fence, the gate hung open into the field. */
function fieldGatePiece(g: THREE.Group, join: number, along: number, rnd: () => number) {
  const axis = (join & 10) ? 10 : (join & 5) ? 5 : along;
  const r = new THREE.Group(); r.rotation.y = axis === 10 ? 0 : Math.PI / 2; g.add(r);
  const wood = 0x8a7a5a, side = rnd() < 0.5 ? -1 : 1;
  addMesh(r, G.sharp(0.15, 1.15, 0.15), dim(wood, 0.8), side * 0.46, 0.57, 0);
  const leaf = new THREE.Group(); leaf.position.set(side * 0.42, 0, 0); leaf.rotation.y = side * (0.7 + rnd() * 0.5); r.add(leaf);
  for (const y of [0.25, 0.42, 0.59, 0.76, 0.93]) addMesh(leaf, G.sharp(0.86, 0.05, 0.04), wood, -side * 0.43, y, 0);
  for (const x of [0.03, 0.43, 0.83]) addMesh(leaf, G.sharp(0.06, 0.74, 0.05), wood, -side * x, 0.59, 0.01);
  const brace = addMesh(leaf, G.sharp(0.95, 0.05, 0.04), wood, -side * 0.43, 0.59, 0.02); brace.rotation.z = side * 0.7;
}

/** A gate across its run: stout posts where it meets a wall, a beam over the top, and a leaf hung open from each post. */
function gatePiece(g: THREE.Group, join: number, along: number, rnd: () => number) {
  const axis = (join & 10) ? 10 : (join & 5) ? 5 : along;
  const r = new THREE.Group(); r.rotation.y = axis === 10 ? 0 : Math.PI / 2; g.add(r);
  // in the run's own frame +x is east (axis 10) or north (axis 5)
  const [plus, minus] = axis === 10 ? [2, 8] : [1, 4];
  const gateAt = (bit: number) => !!(join & (bit << 4));
  const leafCol = SHEETS[Math.floor(rnd() * SHEETS.length)];
  addMesh(r, G.sharp(1.0, 0.12, 0.14), TIMBER, 0, 1.72, 0);
  for (const [side, bit] of [[-1, minus], [1, plus]] as const) {
    if (gateAt(bit)) continue;
    addMesh(r, G.sharp(0.18, 1.85, 0.18), TIMBER, side * 0.45, 0.92, 0);
    addMesh(r, G.sharp(0.24, 0.05, 0.24), STEEL_D, side * 0.45, 1.86, 0);
    // the leaf, swung open towards the outside of the run
    const leaf = new THREE.Group(); leaf.position.set(side * 0.38, 0, 0.06); leaf.rotation.y = side * 1.25; r.add(leaf);
    addMesh(leaf, G.sharp(0.46, 1.08, 0.04), leafCol, -side * 0.23, 0.66, 0);
    ribs(leaf, -side * 0.23, 0.66, 0.025, 0.42, 1.0, dim(leafCol, 0.78), true, 0.12);
    for (const y of [0.3, 1.05]) addMesh(leaf, G.sharp(0.46, 0.06, 0.06), TIMBER, -side * 0.23, y, -0.04);
  }
}
